<template>
  <svg
    ref="svgRef"
    class="annotation-canvas"
    :class="{
      'annotation-canvas--readonly': readonly,
      'annotation-canvas--marker': !readonly && POINT_TOOLS.includes(activeTool),
    }"
    :viewBox="`0 0 ${width} ${height}`"
    :preserveAspectRatio="cover ? 'xMidYMid slice' : 'xMidYMid meet'"
    @mousedown.prevent="onMouseDown"
    @mousemove="onMouseMove"
  >
    <g
      v-for="{ annotation, index } in sortedAnnotations"
      :key="annotation.id"
      class="annotation-canvas__annotation"
      :class="{
        'annotation-canvas__annotation--selected': selectedIndex === index,
        'annotation-canvas__annotation--highlighted': isHighlighted(annotation, index),
        'annotation-canvas__annotation--dimmed': isDimmed(annotation, index),
        'annotation-canvas__annotation--shape-highlighted':
          highlightShapes && isShape(annotation) && (selectedIndex === index || isHighlighted(annotation, index)),
      }"
      @mouseenter="
        !isDrawing &&
        ((hoveredIndex = index), emit('hover', { index, linkedEntityId: annotation.linkedEntityId ?? null }))
      "
      @mouseleave="!isDrawing && ((hoveredIndex = null), emit('hover', null))"
      @click="emit('annotation-click', { index, linkedEntityId: annotation.linkedEntityId ?? null })"
    >
      <!-- Point -->
      <template v-if="isPointGeometry(annotation.geometry)">
        <circle
          :cx="getPointPos(index).x"
          :cy="getPointPos(index).y"
          :r="circleRadius"
          :style="{ strokeWidth }"
          class="annotation-canvas__circle"
          :class="{ 'annotation-canvas__circle--selected': selectedIndex === index }"
          @mousedown.stop="onAnnotationMouseDown(index, 'p1', $event)"
        />
        <text
          v-if="getLabel(annotation, index)"
          :x="getPointPos(index).x"
          :y="getPointPos(index).y"
          dy="0.35em"
          :style="{ fontSize: `${getLabelFontSize(getLabel(annotation, index))}px` }"
          class="annotation-canvas__label"
        >
          {{ getLabel(annotation, index) }}
        </text>
      </template>

      <!-- Line (with curve preview when dragging control point) -->
      <template v-else-if="isLineGeometry(annotation.geometry)">
        <!-- Invisible wide hit area for hovering and clicking the line -->
        <line
          :x1="getTwoPointPos(annotation.geometry, index, 'p1').x"
          :y1="getTwoPointPos(annotation.geometry, index, 'p1').y"
          :x2="getTwoPointPos(annotation.geometry, index, 'p2').x"
          :y2="getTwoPointPos(annotation.geometry, index, 'p2').y"
          :stroke-width="circleRadius * 2"
          stroke="transparent"
          fill="none"
        />
        <!-- Show curve preview when dragging the midpoint control handle -->
        <path
          v-if="isDraggingLineCp(index)"
          :d="`M ${getTwoPointPos(annotation.geometry, index, 'p1').x} ${getTwoPointPos(annotation.geometry, index, 'p1').y} Q ${drawCurrent!.x} ${drawCurrent!.y} ${getTwoPointPos(annotation.geometry, index, 'p2').x} ${getTwoPointPos(annotation.geometry, index, 'p2').y}`"
          fill="none"
          :stroke="getColor(annotation)"
          :stroke-width="getStrokeWidth(annotation)"
        />
        <line
          v-else
          :x1="getTwoPointPos(annotation.geometry, index, 'p1').x"
          :y1="getTwoPointPos(annotation.geometry, index, 'p1').y"
          :x2="getTwoPointPos(annotation.geometry, index, 'p2').x"
          :y2="getTwoPointPos(annotation.geometry, index, 'p2').y"
          :style="{ strokeWidth: getStrokeWidth(annotation) }"
          class="annotation-canvas__line"
          :class="{ 'annotation-canvas__line--selected': selectedIndex === index }"
        />
        <template v-for="ep in ['p1', 'p2'] as const" :key="ep">
          <circle
            :cx="getTwoPointPos(annotation.geometry, index, ep).x"
            :cy="getTwoPointPos(annotation.geometry, index, ep).y"
            :r="circleRadius"
            :style="{ strokeWidth }"
            class="annotation-canvas__circle"
            :class="{ 'annotation-canvas__circle--selected': selectedIndex === index }"
            @mousedown.stop="onAnnotationMouseDown(index, ep, $event)"
          />
          <text
            v-if="getLabel(annotation, index)"
            :x="getTwoPointPos(annotation.geometry, index, ep).x"
            :y="getTwoPointPos(annotation.geometry, index, ep).y"
            dy="0.35em"
            :style="{ fontSize: `${getLabelFontSize(getLabel(annotation, index))}px` }"
            class="annotation-canvas__label"
          >
            {{ getLabel(annotation, index) }}
          </text>
        </template>
        <!-- Dragged control point indicator -->
        <circle
          v-if="isDraggingLineCp(index)"
          :cx="drawCurrent!.x"
          :cy="drawCurrent!.y"
          :r="circleRadius * 0.6"
          fill="none"
          :stroke="getColor(annotation)"
          :stroke-width="strokeWidth"
          stroke-dasharray="3 3"
          class="annotation-canvas__circle"
        />
        <!-- Midpoint control handle (shown on hover, drag to convert to curve) -->
        <circle
          v-else-if="!readonly && hoveredIndex === index"
          :cx="
            (getTwoPointPos(annotation.geometry, index, 'p1').x + getTwoPointPos(annotation.geometry, index, 'p2').x) /
            2
          "
          :cy="
            (getTwoPointPos(annotation.geometry, index, 'p1').y + getTwoPointPos(annotation.geometry, index, 'p2').y) /
            2
          "
          :r="circleRadius * 0.6"
          fill="none"
          :stroke="getColor(annotation)"
          :stroke-width="strokeWidth"
          stroke-dasharray="3 3"
          class="annotation-canvas__circle"
          @mousedown.stop="onAnnotationMouseDown(index, 'cp', $event)"
        />
      </template>

      <!-- Arrow -->
      <template v-else-if="isArrowGeometry(annotation.geometry)">
        <line
          :x1="s(annotation.geometry.x1, width)"
          :y1="s(annotation.geometry.y1, height)"
          :x2="arrowShortenedTip(annotation.geometry).x"
          :y2="arrowShortenedTip(annotation.geometry).y"
          :stroke-width="getStrokeWidth(annotation)"
          :stroke="getColor(annotation)"
          class="annotation-canvas__shape-stroke"
        />
        <polygon
          :points="arrowHeadPoints(annotation.geometry, getStrokeWidth(annotation))"
          :fill="getColor(annotation)"
        />
      </template>

      <!-- Rectangle -->
      <template v-else-if="isRectangleGeometry(annotation.geometry)">
        <rect
          :x="s(annotation.geometry.x, width)"
          :y="s(annotation.geometry.y, height)"
          :width="annotation.geometry.w * width"
          :height="annotation.geometry.h * height"
          fill="none"
          :stroke="getColor(annotation)"
          :stroke-width="getStrokeWidth(annotation)"
        />
      </template>

      <!-- Ellipse -->
      <template v-else-if="isEllipseGeometry(annotation.geometry)">
        <ellipse
          :cx="s(annotation.geometry.cx, width)"
          :cy="s(annotation.geometry.cy, height)"
          :rx="annotation.geometry.rx * width"
          :ry="annotation.geometry.ry * height"
          fill="none"
          :stroke="getColor(annotation)"
          :stroke-width="getStrokeWidth(annotation)"
        />
      </template>

      <!-- Freehand -->
      <template v-else-if="isFreehandGeometry(annotation.geometry)">
        <polyline
          :points="annotation.geometry.points.map((p) => `${s(p.x, width)},${s(p.y, height)}`).join(' ')"
          fill="none"
          :stroke="getColor(annotation)"
          :stroke-width="getStrokeWidth(annotation)"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </template>

      <!-- Highlighter -->
      <template v-else-if="isHighlighterGeometry(annotation.geometry)">
        <g :opacity="annotation.style?.opacity ?? 0.3">
          <polyline
            :points="annotation.geometry.points.map((p) => `${s(p.x, width)},${s(p.y, height)}`).join(' ')"
            fill="none"
            :stroke="getColor(annotation)"
            :stroke-width="getStrokeWidth(annotation) * 4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>
      </template>

      <!-- Curve -->
      <template v-else-if="isCurveGeometry(annotation.geometry)">
        <!-- Invisible wide hit area for hovering and clicking the curve -->
        <path
          :d="getCurvePath(annotation.geometry, index)"
          fill="none"
          stroke="transparent"
          :stroke-width="circleRadius * 2"
        />
        <path
          :d="getCurvePath(annotation.geometry, index)"
          fill="none"
          :stroke="getColor(annotation)"
          :stroke-width="getStrokeWidth(annotation)"
          class="annotation-canvas__shape-stroke"
        />
        <!-- Control point handle -->
        <circle
          v-if="!readonly"
          :cx="getCurveCP(annotation.geometry, index).x"
          :cy="getCurveCP(annotation.geometry, index).y"
          :r="circleRadius * 0.6"
          fill="none"
          :stroke="getColor(annotation)"
          :stroke-width="strokeWidth"
          stroke-dasharray="3 3"
          class="annotation-canvas__circle"
          @mousedown.stop="onAnnotationMouseDown(index, 'cp', $event)"
          @contextmenu.prevent.stop="emit('convert-to-line', { index })"
        />
        <!-- Endpoint circles + labels -->
        <template v-for="ep in ['p1', 'p2'] as const" :key="ep">
          <circle
            :cx="getTwoPointPos(annotation.geometry, index, ep).x"
            :cy="getTwoPointPos(annotation.geometry, index, ep).y"
            :r="circleRadius"
            :style="{ strokeWidth }"
            class="annotation-canvas__circle"
            :class="{ 'annotation-canvas__circle--selected': selectedIndex === index }"
            @mousedown.stop="onAnnotationMouseDown(index, ep, $event)"
          />
          <text
            v-if="getLabel(annotation, index)"
            :x="getTwoPointPos(annotation.geometry, index, ep).x"
            :y="getTwoPointPos(annotation.geometry, index, ep).y"
            dy="0.35em"
            :style="{ fontSize: `${getLabelFontSize(getLabel(annotation, index))}px` }"
            class="annotation-canvas__label"
          >
            {{ getLabel(annotation, index) }}
          </text>
        </template>
      </template>

      <!-- A hovered marker's dustbin, as on the BOM's placements. Inside the marker's group, with a
           see-through strip bridging the gap, so moving onto it keeps the marker hovered. Only
           the button removes: the strip stops at the marker's edge. -->
      <g v-if="hoveredIndex === index && hoveredRemoveBox" class="annotation-canvas__remove" @click.stop>
        <rect v-bind="hoveredRemoveBox.bridge" fill="transparent" />
        <rect
          v-bind="hoveredRemoveBox.button"
          class="annotation-canvas__remove-button"
          @mousedown.stop.prevent="removeAnnotation(index)"
        />
        <svg
          v-bind="hoveredRemoveBox.icon"
          viewBox="0 0 512 512"
          class="annotation-canvas__remove-icon"
          v-html="deleteFilledIcon"
        />
      </g>
    </g>

    <!-- Preview while drawing -->
    <template v-if="isDrawing && !dragging && drawStart && drawCurrent">
      <!-- Freehand/highlighter preview -->
      <template v-if="activeTool === 'freehand' || activeTool === 'highlighter'">
        <polyline
          :points="freehandPoints.map((p) => `${p.x},${p.y}`).join(' ')"
          fill="none"
          class="annotation-canvas__preview-line"
          :stroke-width="activeTool === 'highlighter' ? strokeWidth * 4 : strokeWidth"
          :opacity="activeTool === 'highlighter' ? 0.3 : 1"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </template>
      <!-- Rectangle preview -->
      <template v-else-if="activeTool === 'rectangle'">
        <rect
          :x="Math.min(drawStart.x, drawCurrent.x)"
          :y="Math.min(drawStart.y, drawCurrent.y)"
          :width="Math.abs(drawCurrent.x - drawStart.x)"
          :height="Math.abs(drawCurrent.y - drawStart.y)"
          fill="none"
          class="annotation-canvas__preview-line"
        />
      </template>
      <!-- Ellipse preview -->
      <template v-else-if="activeTool === 'ellipse'">
        <ellipse
          :cx="(drawStart.x + drawCurrent.x) / 2"
          :cy="(drawStart.y + drawCurrent.y) / 2"
          :rx="Math.abs(drawCurrent.x - drawStart.x) / 2"
          :ry="Math.abs(drawCurrent.y - drawStart.y) / 2"
          fill="none"
          class="annotation-canvas__preview-line"
        />
      </template>
      <!-- Arrow preview -->
      <template v-else-if="activeTool === 'arrow'">
        <template v-if="drawDistance > POINT_THRESHOLD">
          <line
            :x1="drawStart.x"
            :y1="drawStart.y"
            :x2="drawCurrent.x"
            :y2="drawCurrent.y"
            class="annotation-canvas__preview-line"
          />
          <polygon :points="previewArrowHeadPoints" class="annotation-canvas__preview-arrowhead" />
        </template>
      </template>
      <!-- Default: marker (point/line) preview -->
      <template v-else>
        <line
          v-if="drawDistance > POINT_THRESHOLD"
          :x1="drawStart.x"
          :y1="drawStart.y"
          :x2="drawCurrent.x"
          :y2="drawCurrent.y"
          class="annotation-canvas__preview-line"
        />
        <circle :cx="drawStart.x" :cy="drawStart.y" :r="circleRadius" class="annotation-canvas__preview-circle" />
        <circle
          v-if="drawDistance > POINT_THRESHOLD"
          :cx="drawCurrent.x"
          :cy="drawCurrent.y"
          :r="circleRadius"
          class="annotation-canvas__preview-circle"
        />
      </template>
    </template>
  </svg>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import type {
  AnnotationItem,
  AnnotationGeometry,
  AnnotationStyle,
  CurveGeometry,
  PointGeometry,
  LineGeometry,
} from '@/ApiClient';
import {
  isPointGeometry,
  isLineGeometry,
  isArrowGeometry,
  isRectangleGeometry,
  isEllipseGeometry,
  isFreehandGeometry,
  isHighlighterGeometry,
  isCurveGeometry,
} from '@/ApiClient';
import { POINT_TOOLS, SHAPE_TOOL_BY_GEOMETRY, type AnnotationTool } from './annotation-toolbar.vue';
import deleteFilledIcon from '@icons/other/delete-filled.svg';

const {
  annotations,
  selectedIndex = null,
  highlightedLinkedEntityId = null,
  highlightedAnnotationIndex = null,
  highlightShapes = false,
  width,
  height,
  readonly = false,
  cover = false,
  scale = 1,
  zoom = 1,
  labelFn,
  activeTool = 'marker',
  activeStyle,
  noPlacement = false,
  removable = false,
} = defineProps<{
  annotations: AnnotationItem[];
  selectedIndex?: number | null;
  highlightedLinkedEntityId?: number | null;
  highlightedAnnotationIndex?: number | null;
  // Hovered and selected drawn shapes take the highlight colour too, as markers do
  highlightShapes?: boolean;
  width: number;
  height: number;
  readonly?: boolean;
  cover?: boolean;
  scale?: number;
  zoom?: number;
  labelFn?: (annotation: AnnotationItem, index: number) => string;
  activeTool?: AnnotationTool;
  activeStyle?: AnnotationStyle;
  // Clicking or dragging on the image draws nothing, while existing annotations can still be
  // selected and moved.
  noPlacement?: boolean;
  // Hovering a marker shows a dustbin that removes it
  removable?: boolean;
}>();

const emit = defineEmits<{
  'add-point': [payload: { x: number; y: number }];
  'add-line': [payload: { x1: number; y1: number; x2: number; y2: number }];
  add: [payload: { geometry: AnnotationGeometry; style?: AnnotationStyle }];
  select: [index: number];
  'move-point': [payload: { index: number; x: number; y: number }];
  'move-endpoint': [payload: { index: number; endpoint: 'p1' | 'p2' | 'cp'; x: number; y: number }];
  'convert-to-curve': [payload: { index: number; cx: number; cy: number }];
  'convert-to-line': [payload: { index: number }];
  hover: [payload: { index: number; linkedEntityId: number | null } | null];
  // Any tool, readonly too: lets a host select what an annotation stands for.
  'annotation-click': [payload: { index: number; linkedEntityId: number | null }];
  remove: [index: number];
}>();

const svgRef = ref<SVGSVGElement | null>(null);

const CIRCLE_PX = 13;
const STROKE_PX = 2;
const FONT_PX = 12;
const POINT_THRESHOLD = 4;
const DEFAULT_COLOR = '#35495E';

const scaleFactor = ref(1);

function updateScaleFactor() {
  const svg = svgRef.value;
  if (!svg) return;
  const ctm = svg.getScreenCTM();
  if (!ctm || ctm.a === 0) return;
  scaleFactor.value = 1 / ctm.a;
}

const circleRadius = computed(() => CIRCLE_PX * scale * scaleFactor.value);
const strokeWidth = computed(() => STROKE_PX * scale * scaleFactor.value);
const fontSize = computed(() => FONT_PX * scale * scaleFactor.value);

function getLabelFontSize(label: string | undefined): number {
  if (!label || label.length <= 2) return fontSize.value;
  const usableDiameter = circleRadius.value * 2 - strokeWidth.value * 2;
  const estimatedWidth = label.length * 0.65 * fontSize.value;
  if (estimatedWidth <= usableDiameter) return fontSize.value;
  return (fontSize.value * usableDiameter) / estimatedWidth;
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (svgRef.value) {
    resizeObserver = new ResizeObserver(updateScaleFactor);
    resizeObserver.observe(svgRef.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

watch(() => width, updateScaleFactor);
watch(
  () => zoom,
  () => nextTick(updateScaleFactor)
);

// Scale normalized coordinate to viewBox
function s(normalized: number, dimension: number): number {
  return normalized * dimension;
}

function getColor(annotation: AnnotationItem): string {
  return annotation.style?.color ?? DEFAULT_COLOR;
}

function getStrokeWidth(annotation: AnnotationItem): number {
  return (annotation.style?.strokeWidth ?? 2) * scale * scaleFactor.value;
}

// Drawing state
const isDrawing = ref(false);
const drawStart = ref<{ x: number; y: number } | null>(null);
const drawCurrent = ref<{ x: number; y: number } | null>(null);
const dragging = ref<{ index: number; endpoint: 'p1' | 'p2' | 'cp' } | null>(null);
const hoveredIndex = ref<number | null>(null);
const freehandPoints = ref<{ x: number; y: number }[]>([]);

const drawDistance = computed(() => {
  if (!drawStart.value || !drawCurrent.value) return 0;
  const dx = drawCurrent.value.x - drawStart.value.x;
  const dy = drawCurrent.value.y - drawStart.value.y;
  return Math.sqrt(dx * dx + dy * dy);
});

const hasHighlight = computed(() => highlightedLinkedEntityId != null || highlightedAnnotationIndex != null);

const sortedAnnotations = computed(() => {
  const indexed = annotations.map((annotation, index) => ({ annotation, index }));
  if (!hasHighlight.value) return indexed;
  return [...indexed].sort((a, b) => {
    const aH = isHighlighted(a.annotation, a.index) ? 1 : 0;
    const bH = isHighlighted(b.annotation, b.index) ? 1 : 0;
    return aH - bH;
  });
});

function isHighlighted(annotation: AnnotationItem, index: number): boolean {
  if (highlightedAnnotationIndex != null) return index === highlightedAnnotationIndex;
  if (highlightedLinkedEntityId != null) return annotation.linkedEntityId === highlightedLinkedEntityId;
  return false;
}

// One marker can stand out among its point's others; those stay as they are, and only the rest fade.
function isDimmed(annotation: AnnotationItem, index: number): boolean {
  if (!hasHighlight.value || isHighlighted(annotation, index)) return false;
  return highlightedLinkedEntityId == null || annotation.linkedEntityId !== highlightedLinkedEntityId;
}

// Drawn rather than placed: no label, and its own colour
function isShape(annotation: AnnotationItem): boolean {
  return annotation.geometry.type in SHAPE_TOOL_BY_GEOMETRY;
}

function isDraggingLineCp(index: number): boolean {
  return !!(dragging.value && dragging.value.index === index && dragging.value.endpoint === 'cp' && drawCurrent.value);
}

function getLabel(annotation: AnnotationItem, index: number): string {
  return labelFn?.(annotation, index) ?? '';
}

function getPointPos(index: number): { x: number; y: number } {
  const annotation = annotations[index];
  const geo = annotation.geometry as PointGeometry;

  if (dragging.value && drawCurrent.value && dragging.value.index === index) {
    return { x: drawCurrent.value.x, y: drawCurrent.value.y };
  }

  return { x: geo.x * width, y: geo.y * height };
}

// The dustbin of a hovered marker, in screen pixels: centred above the marker, or below it when
// there is no room above. Lines and curves carry it on their upper end. Drawn shapes have none.
const REMOVE_BUTTON_PX = 32;
const REMOVE_ICON_PX = 17;
const REMOVE_GAP_PX = 4;
const REMOVE_RADIUS_PX = 10;

const hoveredRemoveBox = computed(() =>
  removable && !readonly && !isDrawing.value && hoveredIndex.value !== null ? removeButtonBox(hoveredIndex.value) : null
);

function removeButtonBox(index: number) {
  const geo = annotations[index].geometry;
  let anchor: { x: number; y: number };
  if (isPointGeometry(geo)) anchor = getPointPos(index);
  else if (isLineGeometry(geo) || isCurveGeometry(geo)) {
    const [p1, p2] = [getTwoPointPos(geo, index, 'p1'), getTwoPointPos(geo, index, 'p2')];
    anchor = p1.y <= p2.y ? p1 : p2;
  } else return null;

  const unit = scale * scaleFactor.value;
  const size = REMOVE_BUTTON_PX * unit;
  const gap = REMOVE_GAP_PX * unit;
  const r = circleRadius.value;
  const above = anchor.y - r - gap - size >= 0;
  const x = anchor.x - size / 2;
  const y = above ? anchor.y - r - gap - size : anchor.y + r + gap;
  const iconSize = REMOVE_ICON_PX * unit;
  return {
    button: { x, y, width: size, height: size, rx: REMOVE_RADIUS_PX * unit },
    bridge: { x, y: above ? y + size : anchor.y + r, width: size, height: gap },
    icon: { x: anchor.x - iconSize / 2, y: y + (size - iconSize) / 2, width: iconSize, height: iconSize },
  };
}

function removeAnnotation(index: number) {
  hoveredIndex.value = null;
  emit('hover', null);
  emit('remove', index);
}

function getTwoPointPos(geo: AnnotationGeometry, index: number, endpoint: 'p1' | 'p2'): { x: number; y: number } {
  const g = geo as LineGeometry;
  const baseX = endpoint === 'p1' ? g.x1 : g.x2;
  const baseY = endpoint === 'p1' ? g.y1 : g.y2;

  if (dragging.value && drawCurrent.value && dragging.value.index === index) {
    if (dragging.value.endpoint === endpoint) {
      return { x: drawCurrent.value.x, y: drawCurrent.value.y };
    }
  }

  return { x: baseX * width, y: baseY * height };
}

function getCurveCP(geo: { cx: number; cy: number }, index: number): { x: number; y: number } {
  if (dragging.value && drawCurrent.value && dragging.value.index === index && dragging.value.endpoint === 'cp') {
    return { x: drawCurrent.value.x, y: drawCurrent.value.y };
  }
  return { x: geo.cx * width, y: geo.cy * height };
}

function getCurvePath(geometry: CurveGeometry, index: number): string {
  const p1 = getTwoPointPos(geometry, index, 'p1');
  const cp = getCurveCP(geometry, index);
  const p2 = getTwoPointPos(geometry, index, 'p2');
  return `M ${p1.x} ${p1.y} Q ${cp.x} ${cp.y} ${p2.x} ${p2.y}`;
}

function getSvgPoint(event: MouseEvent): { x: number; y: number } | null {
  const svg = svgRef.value;
  if (!svg) return null;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const svgPt = pt.matrixTransform(ctm.inverse());
  return { x: svgPt.x, y: svgPt.y };
}

function onAnnotationMouseDown(index: number, endpoint: 'p1' | 'p2' | 'cp', event: MouseEvent) {
  if (readonly) return;
  if (event.button === 2) return;
  if (endpoint !== 'cp' && !POINT_TOOLS.includes(activeTool)) return;
  event.preventDefault();
  emit('select', index);

  const point = getSvgPoint(event);
  if (!point) return;

  dragging.value = { index, endpoint };
  isDrawing.value = true;
  drawStart.value = point;
  drawCurrent.value = point;
  window.addEventListener('mouseup', onWindowMouseUp);
}

function onMouseDown(event: MouseEvent) {
  if (readonly || noPlacement) return;
  const point = getSvgPoint(event);
  if (!point) return;
  isDrawing.value = true;
  drawStart.value = point;
  drawCurrent.value = point;

  if (activeTool === 'freehand' || activeTool === 'highlighter') {
    freehandPoints.value = [point];
  }

  window.addEventListener('mouseup', onWindowMouseUp);
}

function onWindowMouseUp() {
  window.removeEventListener('mouseup', onWindowMouseUp);
  onMouseUp();
}

function constrainPoint(point: { x: number; y: number }, start: { x: number; y: number }): { x: number; y: number } {
  if (activeTool === 'rectangle' || activeTool === 'ellipse') {
    // Constrain to square/circle
    const dx = point.x - start.x;
    const dy = point.y - start.y;
    const size = Math.max(Math.abs(dx), Math.abs(dy));
    return { x: start.x + Math.sign(dx) * size, y: start.y + Math.sign(dy) * size };
  }
  // Constrain to horizontal/vertical (nearest axis)
  const dx = Math.abs(point.x - start.x);
  const dy = Math.abs(point.y - start.y);
  return dx > dy ? { x: point.x, y: start.y } : { x: start.x, y: point.y };
}

function onMouseMove(event: MouseEvent) {
  if (!isDrawing.value || readonly) return;
  let point = getSvgPoint(event);
  if (!point) return;

  if (
    event.shiftKey &&
    drawStart.value &&
    !dragging.value &&
    activeTool !== 'freehand' &&
    activeTool !== 'highlighter'
  ) {
    point = constrainPoint(point, drawStart.value);
  }

  drawCurrent.value = point;

  if (activeTool === 'freehand' || activeTool === 'highlighter') {
    freehandPoints.value.push(point);
  }
}

function onMouseUp() {
  if (!isDrawing.value || readonly || !drawStart.value) {
    isDrawing.value = false;
    return;
  }

  // Dragging existing annotation
  if (dragging.value && drawCurrent.value) {
    const { index, endpoint } = dragging.value;
    const annotation = annotations[index];

    if (isPointGeometry(annotation.geometry)) {
      emit('move-point', {
        index,
        x: drawCurrent.value.x / width,
        y: drawCurrent.value.y / height,
      });
    } else if (isLineGeometry(annotation.geometry) && endpoint === 'cp') {
      emit('convert-to-curve', {
        index,
        cx: drawCurrent.value.x / width,
        cy: drawCurrent.value.y / height,
      });
    } else {
      emit('move-endpoint', {
        index,
        endpoint,
        x: drawCurrent.value.x / width,
        y: drawCurrent.value.y / height,
      });
    }

    dragging.value = null;
    isDrawing.value = false;
    drawStart.value = null;
    drawCurrent.value = null;
    hoveredIndex.value = null;
    emit('hover', null);
    return;
  }

  // Creating new annotation
  const style = activeStyle ? { ...activeStyle } : undefined;
  const n = (v: number, dim: number) => v / dim; // normalize

  if (activeTool === 'freehand' || activeTool === 'highlighter') {
    if (freehandPoints.value.length >= 2) {
      const simplified = simplifyPoints(freehandPoints.value, 2 * scaleFactor.value);
      const points = simplified.map((p) => ({ x: n(p.x, width), y: n(p.y, height) }));
      const geometry: AnnotationGeometry =
        activeTool === 'highlighter' ? { type: 'HighlighterGeometry', points } : { type: 'FreehandGeometry', points };
      const highlighterStyle =
        activeTool === 'highlighter'
          ? { ...(style ?? { color: '#35495E', strokeWidth: 2, opacity: 1 }), opacity: 0.5 }
          : style;
      emit('add', { geometry, style: highlighterStyle });
    }
    freehandPoints.value = [];
  } else if (activeTool === 'rectangle' && drawCurrent.value) {
    const x = n(Math.min(drawStart.value.x, drawCurrent.value.x), width);
    const y = n(Math.min(drawStart.value.y, drawCurrent.value.y), height);
    const w = Math.abs(drawCurrent.value.x - drawStart.value.x) / width;
    const h = Math.abs(drawCurrent.value.y - drawStart.value.y) / height;
    if (w > 0.005 || h > 0.005) {
      emit('add', { geometry: { type: 'RectangleGeometry', x, y, w, h }, style });
    }
  } else if (activeTool === 'ellipse' && drawCurrent.value) {
    const cx = n((drawStart.value.x + drawCurrent.value.x) / 2, width);
    const cy = n((drawStart.value.y + drawCurrent.value.y) / 2, height);
    const rx = Math.abs(drawCurrent.value.x - drawStart.value.x) / 2 / width;
    const ry = Math.abs(drawCurrent.value.y - drawStart.value.y) / 2 / height;
    if (rx > 0.005 || ry > 0.005) {
      emit('add', { geometry: { type: 'EllipseGeometry', cx, cy, rx, ry }, style });
    }
  } else if (activeTool === 'comment') {
    // A free comment is always a point, where the press started
    emit('add-point', { x: drawStart.value.x / width, y: drawStart.value.y / height });
  } else if (activeTool === 'arrow' && drawCurrent.value && drawDistance.value > POINT_THRESHOLD) {
    emit('add', {
      geometry: {
        type: 'ArrowGeometry',
        x1: n(drawStart.value.x, width),
        y1: n(drawStart.value.y, height),
        x2: n(drawCurrent.value.x, width),
        y2: n(drawCurrent.value.y, height),
      },
      style,
    });
  } else {
    // Default: point/line (legacy behavior for MChart/BOM compatibility)
    if (drawDistance.value <= POINT_THRESHOLD) {
      emit('add-point', {
        x: drawStart.value.x / width,
        y: drawStart.value.y / height,
      });
    } else if (drawCurrent.value) {
      emit('add-line', {
        x1: drawStart.value.x / width,
        y1: drawStart.value.y / height,
        x2: drawCurrent.value.x / width,
        y2: drawCurrent.value.y / height,
      });
    }
  }

  isDrawing.value = false;
  drawStart.value = null;
  drawCurrent.value = null;
}

// Ramer-Douglas-Peucker point simplification
function simplifyPoints(points: { x: number; y: number }[], epsilon: number): { x: number; y: number }[] {
  if (points.length <= 2) return points;

  let maxDist = 0;
  let maxIndex = 0;
  const start = points[0];
  const end = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], start, end);
    if (dist > maxDist) {
      maxDist = dist;
      maxIndex = i;
    }
  }

  if (maxDist > epsilon) {
    const left = simplifyPoints(points.slice(0, maxIndex + 1), epsilon);
    const right = simplifyPoints(points.slice(maxIndex), epsilon);
    return [...left.slice(0, -1), ...right];
  }

  return [start, end];
}

function perpendicularDistance(
  point: { x: number; y: number },
  lineStart: { x: number; y: number },
  lineEnd: { x: number; y: number }
): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.sqrt((point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2);
  return Math.abs(dy * point.x - dx * point.y + lineEnd.x * lineStart.y - lineEnd.y * lineStart.x) / Math.sqrt(lenSq);
}

// Arrow helpers
function computeArrowHead(x1: number, y1: number, x2: number, y2: number, headLen: number, headWidth: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1)
    return { tip: { x: x2, y: y2 }, left: { x: x2, y: y2 }, right: { x: x2, y: y2 }, base: { x: x2, y: y2 } };
  const ux = dx / len;
  const uy = dy / len;
  const base = { x: x2 - ux * headLen, y: y2 - uy * headLen };
  const left = { x: base.x - uy * headWidth, y: base.y + ux * headWidth };
  const right = { x: base.x + uy * headWidth, y: base.y - ux * headWidth };
  return { tip: { x: x2, y: y2 }, left, right, base };
}

function arrowShortenedTip(geo: { x1: number; y1: number; x2: number; y2: number }) {
  const headLen = 12 * scale * scaleFactor.value;
  const x1 = s(geo.x1, width),
    y1 = s(geo.y1, height);
  const x2 = s(geo.x2, width),
    y2 = s(geo.y2, height);
  const dx = x2 - x1,
    dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1) return { x: x2, y: y2 };
  return { x: x2 - (dx / len) * headLen, y: y2 - (dy / len) * headLen };
}

function arrowHeadPoints(geo: { x1: number; y1: number; x2: number; y2: number }, sw: number) {
  const headLen = Math.max(12 * scale * scaleFactor.value, sw * 4);
  const headWidth = headLen * 0.45;
  const head = computeArrowHead(
    s(geo.x1, width),
    s(geo.y1, height),
    s(geo.x2, width),
    s(geo.y2, height),
    headLen,
    headWidth
  );
  return `${head.tip.x},${head.tip.y} ${head.left.x},${head.left.y} ${head.right.x},${head.right.y}`;
}

const previewArrowHeadPoints = computed(() => {
  if (!drawStart.value || !drawCurrent.value) return '';
  const headLen = 12 * scale * scaleFactor.value;
  const headWidth = headLen * 0.45;
  const head = computeArrowHead(
    drawStart.value.x,
    drawStart.value.y,
    drawCurrent.value.x,
    drawCurrent.value.y,
    headLen,
    headWidth
  );
  return `${head.tip.x},${head.tip.y} ${head.left.x},${head.left.y} ${head.right.x},${head.right.y}`;
});
</script>

<style scoped lang="scss">
@import '@styles/variables';

$highlight: var(--annotation-highlight, #{$c-blue});

// --annotation-highlight (and --annotation-highlight-text for a label on it) recolour hovered,
// selected and drawn-in-progress marks for a page; blue otherwise
.annotation-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;

  &--readonly {
    cursor: default;

    .annotation-canvas__circle {
      cursor: default;
    }
  }

  &--marker {
    cursor: default;
  }

  &__annotation {
    &--dimmed {
      opacity: 0.25;
    }

    // A drawn shape keeps its own colour otherwise
    &--shape-highlighted {
      rect,
      ellipse,
      polyline,
      line {
        stroke: $highlight;
      }

      polygon {
        fill: $highlight;
      }
    }

    &--highlighted {
      .annotation-canvas__circle {
        stroke: $highlight;
        fill: $highlight;
      }

      .annotation-canvas__line {
        stroke: $highlight;
        stroke-width: 4;
      }

      .annotation-canvas__label {
        fill: var(--annotation-highlight-text, white);
      }
    }
  }

  // As the BOM's placement menu
  &__remove-button {
    fill: white;
    filter: drop-shadow(0 0.2rem 0.5rem rgba(0, 0, 0, 0.15));
    cursor: pointer;
    transition: fill $transition-fast ease;

    &:hover {
      fill: $c-dark-lightened-90;
    }
  }

  &__remove-icon {
    fill: $c-dark;
    pointer-events: none;
  }

  &__circle {
    fill: white;
    stroke: $c-dark;
    cursor: grab;

    &--selected {
      stroke: $highlight;
    }
  }

  &__line {
    stroke: $c-dark;
    pointer-events: none;

    &--selected {
      stroke: $highlight;
    }
  }

  &__shape-stroke {
    pointer-events: none;
  }

  &__label {
    font-family: 'Montserrat', sans-serif;
    font-size: 1.2rem;
    font-weight: 700;
    fill: $c-dark;
    text-anchor: middle;
    pointer-events: none;
    user-select: none;
  }

  &__preview-line {
    stroke: $highlight;
    stroke-width: 2;
    stroke-dasharray: 4 4;
    pointer-events: none;
  }

  &__preview-circle {
    fill: color-mix(in srgb, $highlight 20%, transparent);
    stroke: $highlight;
    stroke-width: 2;
    pointer-events: none;
  }

  &__preview-arrowhead {
    fill: $highlight;
    opacity: 0.6;
    pointer-events: none;
  }
}
</style>
